/**
 * Device profile types + mock data
 * In production, this comes from /api/device/profiles
 */

export type DeviceStatus = "ACTIVE" | "OFFLINE" | "MAINTENANCE" | "ERROR";
export type Transport = "HL7" | "ASTM" | "IEEE_11073" | "DICOM" | "PROPRIETARY";

export interface DeviceProfile {
  id: string;
  vendor: string;
  model: string;
  firmware: string;
  transport: Transport;
  driver: string;
  host: string;
  port: number;
  enabled: boolean;
  status: DeviceStatus;
  lastHeartbeat: string;
  mappingVersion: string;
  iec62304Class: "A" | "B" | "C";
  iec11073Conformant: boolean;
  recentLogs: DeviceLog[];
}

export interface DeviceLog {
  id: string;
  timestamp: string;
  deviceId: string;
  deviceLabel: string;
  patientMrn?: string;
  observation: string;
  result: "SUCCESS" | "WARNING" | "ERROR";
  durationMs: number;
}

const VENDORS = [
  { vendor: "Roche", model: "Cobas e411", transport: "ASTM" as Transport, driver: "dikdoc-driver-roche-cobas-v2.3.1" },
  { vendor: "Siemens", model: "Atellica IM", transport: "HL7" as Transport, driver: "dikdoc-driver-siemens-atellica-v1.8.0" },
  { vendor: "Mindray", model: "BC-6800", transport: "ASTM" as Transport, driver: "dikdoc-driver-mindray-hematology-v3.0.2" },
  { vendor: "Philips", model: "IntelliVue MX800", transport: "IEEE_11073" as Transport, driver: "dikdoc-driver-philips-intellivue-v2.1.4" },
  { vendor: "GE", model: "CARESCAPE B650", transport: "IEEE_11073" as Transport, driver: "dikdoc-driver-ge-carescape-v1.5.0" },
  { vendor: "Dräger", model: "Infinity M540", transport: "HL7" as Transport, driver: "dikdoc-driver-draeger-infinity-v2.0.1" },
  { vendor: "Medtronic", model: "Guardian Connect", transport: "PROPRIETARY" as Transport, driver: "dikdoc-driver-medtronic-guardian-v1.2.3" },
  { vendor: "Dexcom", model: "G6 CGM", transport: "PROPRIETARY" as Transport, driver: "dikdoc-driver-dexcom-g6-v0.9.7" },
  { vendor: "Abbott", model: "FreeStyle Libre 3", transport: "PROPRIETARY" as Transport, driver: "dikdoc-driver-abbott-libre-v1.1.0" },
  { vendor: "Fujifilm", model: "FCR Carbon XL", transport: "DICOM" as Transport, driver: "dikdoc-driver-fujifilm-fcr-v2.0.5" },
  { vendor: "Hologic", model: "Selenia Dimensions", transport: "DICOM" as Transport, driver: "dikdoc-driver-hologic-selenia-v1.4.0" },
  { vendor: "Olympus", model: "CV-290", transport: "DICOM" as Transport, driver: "dikdoc-driver-olympus-cv290-v1.0.3" },
];

function makeLogs(deviceId: string, vendor: string, model: string): DeviceLog[] {
  const samples = [
    { obs: "HbA1c", result: "SUCCESS" as const },
    { obs: "Glucose", result: "SUCCESS" as const },
    { obs: "SpO2", result: "SUCCESS" as const },
    { obs: "Heart rate", result: "SUCCESS" as const },
    { obs: "Blood pressure", result: "WARNING" as const },
    { obs: "Temperature", result: "SUCCESS" as const },
    { obs: "Respiratory rate", result: "SUCCESS" as const },
    { obs: "ECG lead II", result: "ERROR" as const },
    { obs: "ETCO2", result: "SUCCESS" as const },
  ];
  const patients = ["P1234", "P2451", "P0983", "P4422", "P6710", "P1199", "P0055"];
  const now = Date.now();
  return samples.map((s, i) => ({
    id: `${deviceId}-log-${i}`,
    timestamp: new Date(now - i * 30 * 60 * 1000).toISOString(),
    deviceId,
    deviceLabel: `${vendor} ${model}`,
    patientMrn: patients[i % patients.length],
    observation: s.obs,
    result: s.result,
    durationMs: 80 + Math.floor(Math.random() * 400),
  }));
}

export function getMockProfiles(): DeviceProfile[] {
  return VENDORS.map((v, i) => {
    const statuses: DeviceStatus[] = ["ACTIVE", "ACTIVE", "ACTIVE", "OFFLINE", "MAINTENANCE", "ERROR", "ACTIVE"];
    const status = statuses[i % statuses.length];
    return {
      id: `dev-${i.toString().padStart(3, "0")}`,
      vendor: v.vendor,
      model: v.model,
      firmware: `${1 + (i % 3)}.${i % 10}.${(i * 3) % 10}`,
      transport: v.transport,
      driver: v.driver,
      host: `10.42.${1 + (i % 5)}.${10 + i}`,
      port: [2575, 2576, 104, 11112, 4242, 8443][i % 6],
      enabled: status !== "OFFLINE",
      status,
      lastHeartbeat:
        status === "OFFLINE"
          ? new Date(Date.now() - 24 * 3600 * 1000).toISOString()
          : new Date(Date.now() - (i + 1) * 60 * 1000).toISOString(),
      mappingVersion: `v${2 + (i % 3)}.${i % 5}.${(i * 7) % 10}`,
      iec62304Class: (["A", "B", "C"] as const)[i % 3],
      iec11073Conformant: v.transport === "IEEE_11073",
      recentLogs: makeLogs(`dev-${i.toString().padStart(3, "0")}`, v.vendor, v.model),
    };
  });
}

export const STATUS_TONE: Record<DeviceStatus, "default" | "secondary" | "destructive" | "outline"> = {
  ACTIVE: "default",
  OFFLINE: "outline",
  MAINTENANCE: "secondary",
  ERROR: "destructive",
};

export const STATUS_LABEL: Record<DeviceStatus, string> = {
  ACTIVE: "Actif",
  OFFLINE: "Hors ligne",
  MAINTENANCE: "Maintenance",
  ERROR: "Erreur",
};

export const TRANSPORT_LABEL: Record<Transport, string> = {
  HL7: "HL7 v2.x",
  ASTM: "ASTM E1394",
  IEEE_11073: "IEEE 11073",
  DICOM: "DICOM",
  PROPRIETARY: "Propriétaire",
};
